import * as HoverCard from '@radix-ui/react-hover-card'
import { BookMarked, FlaskConical, Folder } from 'lucide-react'
import { Badge } from '@/components/ui/Badge'
import { cn, emptyDash, formatDate } from '@/lib/utils'

export interface BrowseRecord {
  id: string
  code: string
  kind: 'experiment' | 'reference'
  title: string
  swatchHex: string | null
  researcherName: string | null
  experimentDate: string | null
  referenceYear: number | null
  resLabL: number | null
  resLabA: number | null
  resLabB: number | null
  texWeave: string | null
  publishedAt: string | null
  dyeSourceName: string | null
  dyeSourceScientific: string | null
  fiberName: string | null
  manuscriptName: string | null
  institutionName: string | null
  projectName: string | null
}

/**
 * The swatch is the card: colour first, metadata second, which is how researchers
 * scan the grid. The hover card carries the detail that would otherwise force a
 * click into the full record.
 */
export function RecordCard({
  record,
  onOpen,
  selected,
  onToggleSelect,
}: {
  record: BrowseRecord
  onOpen: () => void
  selected?: boolean
  onToggleSelect?: () => void
}) {
  return (
    <HoverCard.Root openDelay={280} closeDelay={80}>
      <HoverCard.Trigger asChild>
        <article
          className={cn(
            'group cursor-pointer overflow-hidden rounded-[var(--radius-card)] border bg-paper transition-shadow',
            selected ? 'border-brand shadow-[var(--shadow-float)]' : 'border-line hover:shadow-[var(--shadow-card)]',
          )}
          onClick={onOpen}
        >
          <div
            className="relative h-28"
            style={{ backgroundColor: record.swatchHex ?? 'var(--color-paper-muted)' }}
          >
            {!record.swatchHex && (
              <span className="absolute inset-0 flex items-center justify-center text-[11px] text-ink-faint">
                No colour recorded
              </span>
            )}
            {onToggleSelect && (
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation()
                  onToggleSelect()
                }}
                className={cn(
                  'absolute right-2 top-2 rounded-full border px-2 py-0.5 text-[11px] font-semibold backdrop-blur transition-colors',
                  selected
                    ? 'border-brand bg-brand text-white'
                    : 'border-white/50 bg-white/75 text-ink opacity-0 group-hover:opacity-100',
                )}
              >
                {selected ? 'Selected' : 'Compare'}
              </button>
            )}
          </div>

          <div className="p-3">
            <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-ink-faint">
              {record.kind === 'reference' ? (
                <BookMarked className="size-3" />
              ) : (
                <FlaskConical className="size-3" />
              )}
              {record.code}
            </p>
            <h3 className="mt-0.5 truncate font-display text-[15px] font-semibold text-ink">
              {record.title || 'Untitled'}
            </h3>
            <p className="mt-0.5 truncate text-[12px] text-ink-muted">
              {emptyDash(record.dyeSourceName)}
              {record.fiberName ? ` · ${record.fiberName}` : ''}
            </p>
            {record.kind === 'experiment' && record.projectName && (
              <p className="mt-0.5 flex items-center gap-1 truncate text-[11px] text-ink-faint">
                <Folder className="size-3 shrink-0" />
                <span className="truncate">{record.projectName}</span>
              </p>
            )}
          </div>
        </article>
      </HoverCard.Trigger>

      <HoverCard.Portal>
        <HoverCard.Content
          side="right"
          sideOffset={8}
          className="z-50 w-72 rounded-[var(--radius-card)] border border-line bg-paper p-4 shadow-[var(--shadow-float)]"
        >
          <header className="flex items-start gap-3">
            <span
              className="size-11 shrink-0 rounded-[var(--radius-control)] border border-line"
              style={{ backgroundColor: record.swatchHex ?? 'var(--color-paper-muted)' }}
            />
            <div className="min-w-0">
              <p className="font-display text-[15px] font-semibold leading-tight text-ink">
                {record.title || 'Untitled'}
              </p>
              <p className="mt-0.5 text-[12px] text-ink-muted">{record.code}</p>
            </div>
          </header>

          <dl className="mt-3 space-y-1 text-[12px]">
            <Row label="Dye source" value={record.dyeSourceName} italic={record.dyeSourceScientific} />
            <Row label="Fiber" value={record.fiberName} />
            <Row label="Weave" value={record.texWeave} />
            <Row label="Researcher" value={record.researcherName} />
            <Row label="Institution" value={record.institutionName} />
            {record.kind === 'experiment' && <Row label="Project" value={record.projectName} />}
            {record.kind === 'reference' ? (
              <>
                <Row label="Manuscript" value={record.manuscriptName} />
                <Row label="Year" value={record.referenceYear} />
              </>
            ) : (
              <Row label="Date" value={record.experimentDate ? formatDate(record.experimentDate) : null} />
            )}
          </dl>

          {record.resLabL !== null && (
            <p className="mt-3 border-t border-line pt-2 font-mono text-[11px] text-ink-muted">
              L* {record.resLabL} a* {record.resLabA} b* {record.resLabB}
            </p>
          )}

          <Badge tone={record.kind === 'reference' ? 'info' : 'neutral'} className="mt-3">
            {record.kind === 'reference' ? 'Historical reference' : 'Experiment'}
          </Badge>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  )
}

function Row({
  label,
  value,
  italic,
}: {
  label: string
  value: string | number | null | undefined
  italic?: string | null
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ink-faint">{label}</dt>
      <dd className="truncate text-right text-ink">
        {value}
        {italic && <span className="ml-1 italic text-ink-faint">{italic}</span>}
      </dd>
    </div>
  )
}
